import { FunctionComponent, useEffect, useState } from "react";
import { DialogContent, Grid, TextField, FormHelperText, IconButton, DialogActions, Button, CircularProgress, Checkbox } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { Controller, useForm } from "react-hook-form";
import { getDepartments, getUsersDepartment } from "../../../api/ticket/get";
import handleNotification from "../../../utils/handleNotification";
import handleError from "../../../utils/handleError";
import { transferTicket, uploadTicketFile } from "../../../api/ticket/put";
import history from "../../../utils/history";
import { RonadDropzone } from "../../../components/RonadDropzone";
import RonadIcons from "../../../components/iconComponent";

interface CreateTicketModalProps {
  handleCloseModal: () => void
  ticketId: string
}
 
const CreateTicket: FunctionComponent<CreateTicketModalProps> = ({handleCloseModal, ticketId}) => {
  const [loading, setLoading] = useState(false)
  const [department, setDepartment] = useState<string>('')
  const [user, setUser] = useState<string>('')
  const [file, setFile] = useState<any>('');
  const [departments, setDepartments] = useState([])
  const [users, setUsers] = useState([])
  const [isHide, setIsHide] = useState(true)
  const { register, control, errors, handleSubmit, setValue } =
  useForm({
    defaultValues: {
      msg: '',
      category: ''
    },
  });

  useEffect(() => {
    getDepartmentList()
  }, [])

  const getDepartmentList = async () => {
    try {
      const response = await getDepartments({id: 1})
      setDepartments(response.data)
    } catch (error) {
      handleError(error)
    }
  }

  const getUserList = async (id:string) => {
    try{
      const response = await getUsersDepartment({departmentId: id})
      setUsers(response.data)
    }catch(err) {
      handleError(err)
    }
  }

  const handleRemoveFile = () => {
    setFile(null);
    setValue('file', null);
  };

  const handleFileDropped = (acceptedFile: any) => {
    setFile(acceptedFile);
  };

  const onSubmit = async (data: any) => {
    const body = {
      message: data.msg,
      departmentId: department,
      userId: user,
      isHide
    }
    try {
      setLoading(true);

      const res = await transferTicket(ticketId,body)
      file && await uploadFile(file, res.data.content.id, ticketId)
      handleNotification('تیکت با موفقیت منتقل شد', 'success');
      handleCloseModal()
      history.push('/ticketing')
    } catch (err) {
      if (err.response?.status === 400) {
        handleNotification(err.response?.data?.title, 'error');
      }

      handleError(err);
    } finally {
      setFile(null)
      setLoading(false);
      setValue('file', null);
    }
  };

  const uploadFile = async (ticketFile: any, id: number, ticketId: string) => {
    const form = new FormData()
    form.append('file', ticketFile)
    await uploadTicketFile(form, id, ticketId)
  }

  return ( <form onSubmit={handleSubmit(onSubmit)}>
  <DialogContent style={{ paddingTop: '0' }}>
    <Grid container direction="row" spacing={2}>
      <Grid item md={6} lg={6} xs={12}>
        <Autocomplete
          id="department"
          options={departments}
          getOptionLabel={option => option.name}
          disableClearable={true}
          onChange={(e: any, newValue: any | null | undefined) => {
            if (newValue) {
              setDepartment(newValue.id);
              getUserList(newValue.id)
              setValue('user', undefined)
              setUser(undefined)
            }
          }}
          renderInput={params => (
            <TextField
              name="department"
              inputRef={register({ required: true })}
              {...params}
              label="دپارتمان"
              fullWidth
              variant="outlined"
            />
          )}
        />
        <FormHelperText error focused>
          {errors.department && 'دپارتمان الزامی میباشد'}
        </FormHelperText>
      </Grid>
      <Grid item md={6} lg={6} xs={12}>
        <Autocomplete
          id="user"
          options={users}
          disabled={!department}
          onChange={(event: any, newValue: any | null) => {
            setUser(newValue.id);
          }}
          getOptionLabel={option => option?.username}
          disableClearable={true}
          renderInput={params => (
            <TextField
              name="user"
              {...params}
              inputRef={register({ required: false })}
              label="کاربر"
              variant="outlined"
            />
          )}
        />
        <FormHelperText error focused>
          {errors.user && 'کاربر الزامی میباشد'}
        </FormHelperText>
      </Grid>
    </Grid>

    <Controller
      control={control}
      id="msg"
      name="msg"
      rules={{ required: false }}
      error={errors.msg}
      as={
        <TextField
          inputProps={{
            style: {
              minHeight: '120px',
              maxHeight: '240px',
              padding: '0 8px',
            },
          }}
          margin="normal"
          multiline
          label="متن پیام"
          variant="outlined"
          fullWidth
        />
      }
    />
    <div>
      <span style={{fontWeight: 'bold'}}>پیغام محرمانه:</span>
      <label>
        <Checkbox
          checked={isHide}
          onChange={e => setIsHide(e.target.checked)}
          id="isHide"
          color="secondary"
          inputProps={{
            'aria-label': 'secondary checkbox',
          }}
        />
        <small style={{color: '#ADADAD'}}>پیغام های محرمانه را مشتریان نخواهند دید.</small>
      </label>
    </div>
    <RonadDropzone
      register={register}
      onDropped={handleFileDropped}
    />
    {file && (
      <Grid
        style={{
          textAlign: 'left',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <IconButton
          aria-label="close"
          style={{ marginLeft: 'auto' }}
          onClick={handleRemoveFile}
        >
          <RonadIcons name="x-close" color="error"  size="20px"/>
        </IconButton>
        <span style={{ marginTop: '14px' }}>
          <span style={{ fontWeight: 'bold' }}> {file.name} </span>
          <span>( {file.size} KB )</span>
        </span>
      </Grid>
    )}
  </DialogContent>
  <DialogActions>
    <Grid
      container
      spacing={1}
      direction="row-reverse"
      style={{ marginBottom: '8px' }}
    >
      <Grid item>
        <Button
          type="submit"
          variant="contained"
          size="large"
          color="primary"
          style={{ marginLeft: '24px' }}
          disabled={loading}
        >

          {loading ? <CircularProgress style={{ height: "30px", width: "30px" }} /> : `انتقال تیکت`}
        </Button>
      </Grid>

      {handleCloseModal ? (
        <Grid item>
          <Button
            type="submit"
            variant="contained"
            size="large"
            color="default"
            onClick={handleCloseModal}
          >
            {`بازگشت`}
          </Button>
        </Grid>
      ) : null}
    </Grid>
  </DialogActions>
</form> );
}
 
export default CreateTicket;